import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { ChevronUpDownIcon, CheckIcon } from "@heroicons/react/16/solid"

export type SelectFieldOption = { value: string; label: string }

type Props = {
  value: string
  onChange: (value: string) => void
  options: SelectFieldOption[]
  placeholder?: string
}

/** Custom-styled single-select trigger + portal dropdown, matching the app's text-input styling (unlike a bare native <select>). */
export const SelectField = ({ value, onChange, options, placeholder = "Выберите…" }: Props) => {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const selected = options.find((o) => o.value === value)

  useEffect(() => {
    if (!open) return

    const updatePos = () => {
      const r = btnRef.current?.getBoundingClientRect()
      if (r) setPos({ top: r.bottom + 6, left: r.left, width: r.width })
    }
    updatePos()

    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (btnRef.current?.contains(target) || panelRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }

    window.addEventListener("scroll", updatePos, true)
    window.addEventListener("resize", updatePos)
    document.addEventListener("mousedown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      window.removeEventListener("scroll", updatePos, true)
      window.removeEventListener("resize", updatePos)
      document.removeEventListener("mousedown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  const select = (v: string) => {
    onChange(v)
    setOpen(false)
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 border border-gray-200 rounded-xl px-4 py-3 text-sm text-left bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
      >
        <span className={selected ? "text-gray-800" : "text-gray-300"}>{selected ? selected.label : placeholder}</span>
        <ChevronUpDownIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
      </button>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width }}
          className="z-[70] bg-white border border-gray-200 rounded-xl shadow-lg shadow-black/5 py-1.5 max-h-64 overflow-auto"
        >
          {options.length === 0 ? (
            <p className="text-xs text-gray-400 px-3.5 py-2">Нет вариантов</p>
          ) : (
            options.map((o) => {
              const isActive = o.value === value
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => select(o.value)}
                  className={`w-full flex items-center gap-2 px-3.5 py-2 text-sm text-left transition-colors hover:bg-gray-50 ${
                    isActive ? "text-blue-700 font-semibold" : "text-gray-700"
                  }`}
                >
                  <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                    {isActive && <CheckIcon className="w-4 h-4 text-blue-600" />}
                  </span>
                  {o.label}
                </button>
              )
            })
          )}
        </div>,
        document.body
      )}
    </>
  )
}